import React, { useState, useEffect, useRef } from 'react';
import {
  Avatar,
  AvatarGroup,
  Typography,
  IconButton,
  Tooltip,
  Box,
  Toolbar,
  CircularProgress,
  Chip,
  InputBase,
} from '@mui/material';
import {
  InsertDriveFile as FileIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Compare,
  RotateRight as RotateRightIcon,
  OpenInFull,
  OpenInNew as OpenInNewIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuthenticatedImage } from '../../../../hooks/useAuthenticatedImage';
import { useFileUrl, downloadFileLink } from '../../../../hooks/useFileUrl';
import { ImagePreview } from './ImagePreview';
import { PDFPreview } from './PDFPreview';
import { VideoPreview } from './VideoPreview';
import { FileFullscreenViewer } from "../../../FileFullscreenViewer";
import VersionHistoryDialog from '../../../VersionHistoryDialog';

import { getFileIcon } from '../../../../utils/getFileIcon';
import { getFileIconColor } from '../../../../utils/getFileTypeColor';
import { supportsPreview } from '../../../../utils/supportsPreview';
import { getPreviewKind, isVideoFile } from '../../../../utils/previewKind';
import { getFileTypeLabel } from '../../../../utils/getFileTypeLabel';
import { formatFileSize } from '../../../../utils/formatFileSize';
import { useFiles } from '../../../../hooks/useFiles';
import { isOwnItem } from '../../../../utils/getFolderName';
import { SharedFileMark } from '../FolderGlyph';
import { useFilePermissions } from '../../../../../auth/hooks/usePermissions';



/**
 * FilePreviewSidebar - Sidebar component for previewing file details
 */
export const FilePreviewSidebar = ({
  file,
  onDownload,
  onRename,
  onDelete,
  deleteLabel,
  onCompare,
  compareMode = false,
  side = "center",
  width,
  sx = {},
}) => {
  const { t } = useTranslation(["files", "common"]);
  const [rotation, setRotation] = useState(0);
  const [rotationLoaded, setRotationLoaded] = useState(false);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  const [fileDetails, setFileDetails] = useState(null);
  const [readers, setReaders] = useState([]);
  const [readersLoading, setReadersLoading] = useState(false);

  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [detailsKey, setDetailsKey] = useState(0);

  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [displayFilename, setDisplayFilename] = useState(null); // optimistic override
  const nameInputRef = useRef(null);

  const {
    getFileDetails,
    updateFileMetadata,
    markFileRead,
    getVersionReaders,
  } = useFiles();

  // Another department's shared file: it can be looked at, turned for a better
  // look and downloaded, but only that department changes it. Changing a file
  // also takes file:upload; who read it and its history take file:read.
  const { canRead, canUpload } = useFilePermissions();
  const shared = !isOwnItem(fileDetails || file);
  const readOnly = shared || !canUpload;

  const handleRotate = async () => {
    const newRotation = (rotation + 90) % 360;
    setRotation(newRotation);
    if (updateFileMetadata && file?.file_id && !readOnly) {
      try {
        await updateFileMetadata(file.file_id, { rotation: newRotation });
      } catch (err) {
        console.error('Failed to update rotation metadata:', err);
      }
    }
  };

  const handleDownload = async () => {
    if (onDownload) {
      onDownload(file);
      return;
    }
    await downloadFileLink(file.file_id, { filename: file.filename });
  };

  const handleFullscreen = () => setFullscreenOpen(true);

  const handleOpenInNewTab = () => {
    if (file?.file_id) window.open(`/files/view/${file.file_id}`, '_blank');
  };

  const handleStartRename = () => {
    setNameValue(displayFilename ?? file.filename);
    setEditingName(true);
    setTimeout(() => nameInputRef.current?.select(), 0);
  };

  const handleCommitRename = async () => {
    setEditingName(false);
    const trimmed = nameValue.trim();
    if (!trimmed || trimmed === (displayFilename ?? file.filename)) return;
    try {
      await updateFileMetadata(file.file_id, { filename: trimmed });
      setDisplayFilename(trimmed);
      onRename?.(file, trimmed);
    } catch (err) {
      console.error('Rename failed:', err);
    }
  };

  const handleNameKeyDown = (e) => {
    if (e.key === 'Enter') handleCommitRename();
    if (e.key === 'Escape') setEditingName(false);
  };

  const handleVersionUploaded = () => {
    setFileDetails(null);
    setReaders([]);
    setRotationLoaded(false);
    setDetailsKey((k) => k + 1);
  };

  // Exit rename mode and clear optimistic name when selected file changes
  useEffect(() => { setEditingName(false); setDisplayFilename(null); }, [file?.file_id]);

  // Fetch file details → set rotation → mark current version as read
  useEffect(() => {
    if (!file?.file_id) return;

    setFileDetails(null);
    setRotationLoaded(false);

    (async () => {
      const res = await getFileDetails(file.file_id);
      const data = res?.data?.data;
      setFileDetails(data ?? null);
      setRotation(data?.rotation ?? 0);
      setRotationLoaded(true);
      if (data?.current_version_number) {
        markFileRead(file.file_id, data.current_version_number).catch(() => {});
      }
    })();
  }, [file?.file_id, detailsKey, getFileDetails, markFileRead]);

  // Fetch readers for the latest version only
  useEffect(() => {
    if (!canRead || !file?.file_id || !fileDetails?.current_version_number) {
      setReaders([]);
      return;
    }
    setReadersLoading(true);
    setReaders([]);
    getVersionReaders(file.file_id, fileDetails.current_version_number, 50)
      .then((res) => setReaders(res?.data?.data?.logs ?? []))
      .catch(() => {})
      .finally(() => setReadersLoading(false));
  }, [canRead, file?.file_id, fileDetails?.current_version_number, getVersionReaders]);


  // PDFs and videos: signed link the browser streams. Images: a 512px
  // thumbnail (rotated server-side as the user turns it), only after rotation is loaded.
  const streamed = ['pdf', 'video'].includes(getPreviewKind(file?.filetype, file?.filename));
  const thumbnail = useAuthenticatedImage(
    streamed ? null : file,
    '512x512',
    rotationLoaded ? rotation : null,
    fileDetails?.current_version_number ?? null
  );
  const link = useFileUrl(streamed ? file?.file_id : null);
  const { imageUrl, loading, error } = streamed
    ? { imageUrl: link.url, loading: link.loading, error: link.error }
    : thumbnail;

  // Empty state when no file is selected
  if (!file) {
    return (
      <Box
        sx={{
          height: '100%',
          borderLeft: 1,
          flex: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          gap: 2,
          ...(width ? { width, maxWidth: width } : {}),
          ...sx,
        }}
      >
        <Avatar sx={{ width: 80, height: 80, bgcolor: 'action.hover', color: 'text.secondary' }}>
          <FileIcon sx={{ fontSize: 48 }} />
        </Avatar>
        <Typography variant="h6" color="text.secondary" textAlign="center">
          {t('files:messages.no_file_selected')}
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          {t('files:messages.select_file_to_preview')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        borderLeft: 1,
        flex: 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        ...(width ? { width, maxWidth: width } : {}),
        ...sx,
      }}
    >
      {/* Actions Toolbar - Hidden in compare mode */}
      {!compareMode && (
        <Toolbar
          variant="dense"
          sx={{ borderBottom: 1, borderColor: 'divider', gap: 0.5, minHeight: 48 }}
        >
          <Tooltip title={t('files:actions.download')}>
            <IconButton size="small" onClick={handleDownload} aria-label={t('files:actions.download')}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {onRename && !readOnly && (
            <Tooltip title={t('files:actions.rename')}>
              <IconButton size="small" onClick={handleStartRename} aria-label={t('files:actions.rename')}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {/* Turning only means something for pictures */}
          {supportsPreview(file.filetype) && (
            <Tooltip title={t('files:actions.rotate')}>
              <IconButton size="small" onClick={handleRotate} aria-label={t('files:actions.rotate')}>
                <RotateRightIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {canRead && (
            <Tooltip title={t('files:labels.version_history')}>
              <IconButton size="small" onClick={() => setVersionHistoryOpen(true)} aria-label={t('files:labels.version_history')}>
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && !shared && (
            <Tooltip title={deleteLabel || t('files:actions.delete')}>
              <IconButton
                size="small"
                color="error"
                onClick={() => onDelete?.(file)}
                aria-label={deleteLabel || t('files:actions.delete')}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ flexGrow: 1 }} />
          {onCompare && (
            <Tooltip title={t('files:actions.compare')}>
              <IconButton size="small" onClick={() => onCompare?.(file)}>
                <Compare fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={t('files:actions.fullscreen')}>
            <IconButton size="small" onClick={handleFullscreen} aria-label={t('files:actions.fullscreen')}>
              <OpenInFull fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('files:messages.open_new_tab')}>
            <IconButton size="small" onClick={handleOpenInNewTab} aria-label={t('files:messages.open_new_tab')}>
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Toolbar>
      )}

      {/* Scrollable Content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: compareMode ? 0 : 2, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Name first, so it's clear which file is shown; then version and readers */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {editingName ? (
            <InputBase
              inputRef={nameInputRef}
              value={nameValue}
              onChange={(e) => setNameValue(e.target.value)}
              onBlur={handleCommitRename}
              onKeyDown={handleNameKeyDown}
              autoFocus
              sx={{
                flex: 1,
                minWidth: 0,
                fontWeight: 500,
                fontSize: '1rem',
                borderBottom: '1px solid',
                borderColor: 'primary.main',
              }}
            />
          ) : readOnly ? (
            <>
              <Typography variant="body1" fontWeight={500} noWrap title={file.filename} sx={{ flex: 1, minWidth: 0 }}>
                {file.filename}
              </Typography>
              <SharedFileMark file={fileDetails} />
            </>
          ) : (
            <Tooltip title={t('files:actions.rename')} placement="top">
              <Typography
                variant="body1"
                fontWeight={500}
                noWrap
                onClick={handleStartRename}
                sx={{ flex: 1, minWidth: 0, cursor: 'text', '&:hover': { textDecoration: 'underline' } }}
              >
                {displayFilename ?? file.filename}
              </Typography>
            </Tooltip>
          )}
          {fileDetails?.current_version_number && (
            <Chip
              label={
                `v${fileDetails.current_version_number}`
              }
              size="small"
              variant="outlined"
              sx={{ flexShrink: 0 }}
            />
          )}
          {!compareMode && (
            readersLoading ? (
              <CircularProgress size={20} sx={{ flexShrink: 0 }} />
            ) : (() => {
              const seen = new Set();
              const unique = readers
                .filter((log) => {
                  const key = log.staff_member_id ?? log.staff_member_name;
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                })
                .reverse();
              if (unique.length === 0) return null;
              return (
                <AvatarGroup
                  max={6}
                  sx={{ flexShrink: 0, '& .MuiAvatar-root': { width: 28, height: 28, fontSize: '0.75rem' } }}
                >
                  {unique.map((log) => (
                    <Tooltip
                      key={log.staff_member_id ?? log.log_id}
                      title={`${log.staff_member_name} · ${new Date(log.read_at).toLocaleString()}`}
                      placement="top"
                    >
                      <Avatar>{log.staff_member_name?.charAt(0)?.toUpperCase() ?? '?'}</Avatar>
                    </Tooltip>
                  ))}
                </AvatarGroup>
              );
            })()
          )}
        </Box>
        {!compareMode && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
            {[
              getFileTypeLabel(file),
              formatFileSize(file.file_size),
              file.uploader_name,
              file.uploaded_at && new Date(file.uploaded_at).toLocaleDateString(),
            ].filter(Boolean).join(' · ')}
          </Typography>
        )}

        {/* Preview */}
        {supportsPreview(file.filetype) ? (
          <ImagePreview
            file={file}
            imageUrl={imageUrl}
            loading={loading}
            error={error}
            compareMode={compareMode}
            side={side}
            rotation={rotation}
            onRotate={handleRotate}
          />
        ) : file.filetype === "application/pdf" ? (
          <PDFPreview file={file} imageUrl={imageUrl} loading={loading} error={error} />
        ) : isVideoFile(file.filetype, file.filename) ? (
          <VideoPreview file={file} imageUrl={imageUrl} loading={loading} error={error} />
        ) : (
          <Box
            sx={{
              p: 4,
              bgcolor: 'grey.100',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Avatar
              sx={{ width: 64, height: 64, bgcolor: 'transparent', color: getFileIconColor(file.filetype) }}
            >
              {getFileIcon(file.filetype)}
            </Avatar>
            <Typography variant="caption" color="text.secondary" textAlign="center">
              {t('files:messages.no_preview')}
            </Typography>
          </Box>
        )}

      </Box>

      {/* Fullscreen Viewer */}
      {fullscreenOpen && (
        <FileFullscreenViewer
          fileId={file.file_id}
          filename={file.filename}
          filetype={file.filetype}
          onClose={() => setFullscreenOpen(false)}
        />
      )}

      {versionHistoryOpen && (
        <VersionHistoryDialog
          open={versionHistoryOpen}
          onClose={() => setVersionHistoryOpen(false)}
          file={file}
          fileDetails={fileDetails}
          onVersionUploaded={readOnly ? undefined : handleVersionUploaded}
        />
      )}
    </Box>
  );
};
